import React, { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import {
  Button,
  DialogContent,
  DialogTitle,
  DialogActions,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Typography,
} from '@material-ui/core';
import { useFormik } from 'formik';
import * as yup from 'yup';

import Dialog from 'components/shared/Dialog';
import LoadingIndicator from 'components/shared/LoadingIndicator';
import Alert, { useAlert } from 'components/shared/Alert';
import ConfirmationDialog, {
  useConfirmationDialog,
} from 'components/shared/ConfirmationDialog';
import NumberFormat from 'react-number-format';
import { useUser, sendPasswordReset } from 'api/users';
import { defaultUser, OrganizationItemLink, User } from 'models';
import { useNavigate } from 'react-router-dom';
import SetUserPasswordDialog from './SetUserPasswordDialog';
import UserOrganizationsListView from './UserOrganizationsListView';
import UserOrganizationDetailView from './UserOrganizationDetailView';
import utils from 'utils';

import './EditUserDialog.scss';
import { phoneValidationSchema } from 'common/validation';

interface EditUserDialogProps {
  onClose: (openDialog: boolean) => void;
  refreshActionAfterSave?: () => void;
  userId?: string;
  organizationId?: string;
}

const validationSchema = yup.object({
  name: yup.string().trim().min(1).required(),
  lastName: yup.string().trim().min(1).required(),
  email: yup.string().trim().email().required(),
  jobTitle: yup.string().trim().min(1).required(),
  phone: phoneValidationSchema,
});

const EditUserDialog = (props: EditUserDialogProps) => {
  const { onClose, userId, organizationId } = props;

  const { isAlertOpen, alertMessage, variant, openAlert, closeAlert } =
    useAlert();
  const {
    isConfirmationDialogOpen,
    isConfirmationLoading,
    confirmationMessage,
    onAccept,
    onDeny,
    openConfirmationDialog,
  } = useConfirmationDialog();

  const [initialValues, setInitialValues] = useState(false);
  const [isSetUserPasswordDialogOpen, setIsSetUserPasswordDialogOpen] =
    useState<boolean>(false);
  const {
    query: { data, status },
    mutation,
  } = useUser(userId, organizationId);
  const [user, setUser] = useState<User>(defaultUser);
  const [selectedOrganizationId, setSelectedOrganizationId] =
    useState<string>('');

  const validate = () => {
    if (!user?.organizations?.length) {
      openAlert('Must add at least one organization.', 'error');
      return false;
    }

    if (!user.defaultOrganization) {
      openAlert('Must choose a default organization.', 'error');
      return false;
    }

    if (user.defaultOrganization) {
      const defaultOrgId = user.defaultOrganization.id;
      const isDefaultOrgValid = user.organizations.find(
        (org) => org.id === defaultOrgId
      );

      if (!isDefaultOrgValid) {
        openAlert('Must choose a default organization.', 'error');
        return false;
      }
    }

    const orgWithoutGroup = user.organizations.find(
      (org) => !user.groups?.find((grp) => grp.organization.id === org.id)
    );
    if (orgWithoutGroup) {
      openAlert(
        `Must choose at least one group for organization ${orgWithoutGroup.name}.`,
        'error'
      );
      return false;
    }

    return true;
  };

  const formik = useFormik({
    initialValues: {
      name: '',
      lastName: '',
      phone: '',
      email: '',
      jobTitle: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      if (validate()) {
        try {
          await mutation.mutateAsync({
            ...user,
            firstName: values.name,
            lastName: values.lastName,
            jobTitle: values.jobTitle,
            email: values.email,
            mobilePhone: {
              number: values.phone,
              allowSMS: user.allowSMS,
            },
          });
          onClose(false);
        } catch (error: any) {
          if (error?.message === 'Conflict') {
            openAlert(
              `User with username ${values.email} already exists`,
              `error`
            );
          } else {
            openAlert(`There was an API error`, `error`);
          }
        }
      }
    },
  });

  // TODO move these into a common routing file as constants
  const defaultTabKey = 'groups';

  const navigate = useNavigate();
  useEffect(() => {
    if (user?.organizations && user?.organizations?.length > 0) {
      navigate(`${user.organizations?.[0].id}/${defaultTabKey}`, {
        replace: true,
      });
      setSelectedOrganizationId(user.organizations?.[0].id);
    }
  }, [defaultTabKey, navigate, user.organizations]);

  useEffect(() => {
    if (data && !!data?.id && userId) {
      setUser(data);
    }
  }, [data, setUser, userId]);

  useEffect(() => {
    if (data && !!data?.id && !initialValues) {
      formik.setValues({
        name: data?.firstName ?? '',
        lastName: data?.lastName ?? '',
        phone: data?.mobilePhone?.number ?? '',
        email: data?.email ?? '',
        jobTitle: data?.jobTitle ?? '',
      });
      setInitialValues(true);
    }
  }, [formik, data, initialValues]);

  const flatOrganizations = (
    organizations: OrganizationItemLink[]
  ): OrganizationItemLink[] =>
    organizations.reduce((acc, cur) => {
      acc.push(cur);
      if (cur.children) return acc.concat(flatOrganizations(cur.children));
      return acc;
    }, [] as OrganizationItemLink[]);

  const findOrganizationById = (id: string) => {
    const flatOrgs = flatOrganizations(user?.organizations || []);
    return flatOrgs?.find((org) => org?.id === id);
  };

  const handleSendPasswordResetEmailClick = async () => {
    if (!user) return;

    // TODO: check if email has been saved
    // if (false) {
    //   openConfirmationDialog({
    //     messageOverride: `You edited this user's email address but haven't saved yet. You must save this user before sending the password reset email to the new email address. Would you like to save this user now?`,
    //     stayOpenOnAcceptOverride: true,
    //     onAcceptOverride: async () => {
    //       // const success = await state.save();
    //       // if (!success) return;
    //       openConfirmationDialog({
    //         messageOverride: `Send a password-reset email to this user at ${user.email}?`,
    //         stayOpenOnAcceptOverride: false,
    //         onAcceptOverride: async () => sendPasswordReset(user.id)
    //       });
    //     },
    //   });
    //   return;
    // }

    openConfirmationDialog({
      messageOverride: `Send a password-reset email to this user at ${user.email}?`,
      stayOpenOnAcceptOverride: false,
      onAcceptOverride: async () => sendPasswordReset(user.id),
    });
  };

  return (
    <>
      <Dialog
        open
        className="EditUserDialog"
        fullWidth
        hideCancelButton
        hidePrimaryButton
        maxWidth="lg"
        onSubmit={formik.handleSubmit}
      >
        <DialogTitle>
          <Typography>
            {user?.id ? `Edit User ID: ${user.id}` : 'New User'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <div className="full-height full-width flex-rows">
            <div className="EditUserDialog__topForm full-width side-padding">
              <div className="column">
                <TextField
                  className="form-control"
                  id="EditUserDialog-first-name"
                  label="First Name"
                  name="name"
                  variant="outlined"
                  required
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  margin="dense"
                  error={formik.touched.name && !!formik.errors.name}
                />
                <TextField
                  className="form-control"
                  id="EditUserDialog-last-name"
                  label="Last Name"
                  name="lastName"
                  variant="outlined"
                  required
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  margin="dense"
                  error={formik.touched.lastName && !!formik.errors.lastName}
                />
              </div>
              <div className="column">
                <TextField
                  className="form-control"
                  id="EditUserDialog-job-title"
                  label="Job Title"
                  variant="outlined"
                  name="jobTitle"
                  required
                  value={formik.values.jobTitle}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  margin="dense"
                  error={formik.touched.jobTitle && !!formik.errors.jobTitle}
                />
                <TextField
                  className="form-control"
                  id="EditUserDialog-email"
                  label="Email"
                  variant="outlined"
                  required
                  name="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  margin="dense"
                  error={formik.touched.email && !!formik.errors.email}
                />
              </div>
              <div className="column">
                <div className="right-column-top-row">
                  <NumberFormat
                    id="EditUserDialog-phone"
                    customInput={TextField}
                    className="form-control form-control-phone"
                    label="Phone"
                    name="phone"
                    inputMode="tel"
                    required
                    type="tel"
                    variant="outlined"
                    margin="dense"
                    format="(###) ###-####"
                    mask="_"
                    value={formik.values.phone}
                    onChange={(e) => {
                      const newPhone = utils.strings.cleanPhoneNumber(
                        e.target.value
                      );
                      formik.setFieldValue('phone', newPhone);
                    }}
                    error={formik.touched.phone && !!formik.errors.phone}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={user?.mobilePhone?.allowSMS}
                        onChange={() =>
                          setUser({
                            ...user,
                            mobilePhone: {
                              ...user.mobilePhone,
                              allowSMS: !user?.mobilePhone?.allowSMS,
                            },
                          })
                        }
                      />
                    }
                    label="Allow SMS"
                  />
                </div>
                <div className="right-column-bottom-row">
                  <FormLabel>Status</FormLabel>
                  <RadioGroup
                    row
                    name="status"
                    value={user?.status ?? ''}
                    onChange={(e) =>
                      setUser({ ...user, status: e.target.value })
                    }
                  >
                    <FormControlLabel
                      value="ACTIVE"
                      control={<Radio />}
                      label="Active"
                    />
                    <FormControlLabel
                      value="INACTIVE"
                      control={<Radio />}
                      label="Inactive"
                    />
                  </RadioGroup>
                </div>
              </div>
            </div>
            <div className="flex-grow margin-top border-top border-bottom">
              <div className="EditUserDialog-rooftop-content">
                <UserOrganizationsListView
                  userId={user.id}
                  userOrganizations={user.organizations}
                  selectedOrganizationId={selectedOrganizationId}
                  setSelectedOrganizationId={setSelectedOrganizationId}
                  setUser={setUser}
                />
                <UserOrganizationDetailView
                  organization={findOrganizationById(selectedOrganizationId)}
                  user={user}
                  setUser={setUser}
                />
              </div>
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <>
            {user?.id ? (
              <Button type="button" onClick={handleSendPasswordResetEmailClick}>
                Send Password Reset Email
              </Button>
            ) : (
              <Form.Check
                inline
                id="EditUserDialog.sendWelcomeMessage"
                type="checkbox"
                name="EditUserDialog.sendWelcomeMessage"
                label="Send Welcome Message"
                onChange={() =>
                  setUser({
                    ...user,
                    sendWelcomeMessage: !user.sendWelcomeMessage,
                  })
                }
                checked={user?.sendWelcomeMessage}
              />
            )}
            <Button
              type="button"
              onClick={() => setIsSetUserPasswordDialogOpen(true)}
            >
              Set Password&hellip;
            </Button>
          </>
          <div className="EditUserDialog-divider" />
          <Button
            onClick={() => onClose(false)}
            disabled={status === 'loading'}
          >
            Cancel
          </Button>
          <Button
            id="modal-save-button"
            color="secondary"
            style={{ color: 'white' }}
            variant="contained"
            disabled={status === 'loading'}
            onClick={() => formik.submitForm()}
          >
            {mutation.isLoading ? (
              <LoadingIndicator size={13} color="primary" />
            ) : (
              'Save'
            )}
          </Button>
        </DialogActions>
        {isSetUserPasswordDialogOpen && (
          <SetUserPasswordDialog
            userId={userId || ''}
            onClose={() => setIsSetUserPasswordDialogOpen(false)}
            setFirstPassword={(firstPassword: string) =>
              setUser({ ...user, firstPassword })
            }
          />
        )}
      </Dialog>
      <Alert
        open={isAlertOpen}
        message={alertMessage}
        variant={variant}
        duration={3500}
        onClose={closeAlert}
      />
      <ConfirmationDialog
        open={isConfirmationDialogOpen}
        isLoading={isConfirmationLoading}
        onPrimaryButtonClick={onAccept}
        onSecondaryButtonClick={onDeny}
        message={confirmationMessage}
      />
    </>
  );
};

export default EditUserDialog;
